<script lang="ts" setup>
import { useRoute } from 'vue-router/composables';
import { ref, onMounted, computed } from 'vue';
import { useI18n } from '@/i18n/i18n';
import BcInput from '@/ui-kit/components/BcInput/BcInput.vue';
import BcSpinner from '@/ui-kit/components/BcSpinner/BcSpinner.vue';
import PanelHeader from '@/components/Panel/PanelHeader/PanelHeader.vue';
import { usePanelStore } from '@/store/pinia/panel';
import {
  getCampaignContactedProspects,
  getCampaignAcceptedProspects,
  getCampaignRespondedProspects
} from '@/api/campaigns';
import PanelHeaderNav from '@/components/Panel/PanelHeader/PanelHeaderNav.vue';
import CampaignsStatisticsPanelSearchbar from '@/components/Campaigns/CampaignsStatistics/CampaignsStatisticsPanelSearchbar.vue';
import CampaignStatisticsProspectCard from '@/components/Campaigns/CampaignsStatistics/CampaignStatisticsProspectCard.vue';
import { CampaignProspect, CampaignProspectAction, CampaignStatusKey, campaignActionKeys } from '@/domains/models/Campaign';
import ProspectsMessageViewPanel from '@/components/Prospects/ProspectsMessageViewPanel.vue';

// Extended CampaignProspect type with message fields
type ProspectWithMessages = CampaignProspect & {
  messages: {
    lastSentMessageId?: string;
    customInvitationMessageId?: string;
    receivedMessageId?: string;
  }
}

const route = useRoute();
const panelStore = usePanelStore();
const { t } = useI18n();

const props = defineProps<{
  campaignId: string;
}>();

const prospectType = ref('');
const title = ref('');
const isLoading = ref(true);
const prospects = ref<ProspectWithMessages[]>([]);
const searchQuery = ref('');
const isViewingMessage = ref(false);
const selectedProspect = ref<ProspectWithMessages | null>(null);

// Get prospects data by type
const getProspectsByType = async (type: string, search?: string) => {
  switch (type) {
    case 'contacted':
      return getCampaignContactedProspects(props.campaignId, { search });
    case 'accepted':
      return getCampaignAcceptedProspects(props.campaignId, { search });
    case 'responded':
      return getCampaignRespondedProspects(props.campaignId, { search });
    default:
      throw new Error('Invalid prospect type');
  }
};

// Get the title based on the prospect type
const getTitle = (type: string) => {
  switch (type) {
    case 'contacted':
      return t('campaigns.statistics.headers.contacted');
    case 'accepted':
      return t('campaigns.statistics.headers.accepted-invitation');
    case 'responded':
      return t('campaigns.statistics.headers.answered');
    default:
      return '';
  }
};

// Load prospects data
const loadProspects = async () => {
  isLoading.value = true;
  try {
    const response = await getProspectsByType(prospectType.value, searchQuery.value || undefined);
    const items = response.data.items || [];

    // Add message fields to prospects based on the type
    prospects.value = items.map(prospect => {
      const messages: ProspectWithMessages['messages'] = {
        lastSentMessageId: undefined,
        customInvitationMessageId: undefined,
        receivedMessageId: undefined
      };

      if (prospect._message) {
        messages.receivedMessageId = prospect._message;
      }

      // Define message-kind action types from the constants
      const messageKindTypes = [
        campaignActionKeys.LINKEDIN_MESSAGE,
        campaignActionKeys.LINKEDIN_INMAIL_SALES_NAVIGATOR,
        campaignActionKeys.LINKEDIN_INMAIL_RECRUITER,
        campaignActionKeys.LINKEDIN_INMAIL_PREMIUM,
        campaignActionKeys.LINKEDIN_INVITATION,
        campaignActionKeys.LINKEDIN_VOICE_NOTE
      ];

      const customInvitationAction = prospect.prospectActions.find((action: CampaignProspectAction) =>
        action.campaignAction.type === campaignActionKeys.LINKEDIN_INVITATION && 
        (action.body || action.campaignAction?.body) && 
        action._message
      );
      messages.customInvitationMessageId = customInvitationAction?._message;

      // Get last message from message-kind types using Array.prototype.findLast or reverse().find()
      // First try with findLast
      let lastMessageAction;

      lastMessageAction = prospect.prospectActions.findLast((action: CampaignProspectAction) =>
        messageKindTypes.includes(action.campaignAction.type) && action._message
      );

      messages.lastSentMessageId = lastMessageAction?._message;

      return {
        ...prospect,
        messages
      };
    });
  } catch (error) {
    console.error('Error loading prospects:', error);
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  // Get prospect type from props
  prospectType.value = panelStore.modalProps.type || 'contacted';
  title.value = getTitle(prospectType.value);
  loadProspects();
});

// Close the panel
const closePanel = () => {
  panelStore.closeModal();
};

// View message for a prospect
const viewMessage = (prospect: ProspectWithMessages) => {
  selectedProspect.value = prospect;
  isViewingMessage.value = true;
};

// Go back to prospects list
const goBackToList = () => {
  isViewingMessage.value = false;
  selectedProspect.value = null;
};
</script>

<template>
  <!-- Message View Panel -->
  <ProspectsMessageViewPanel v-if="isViewingMessage && selectedProspect" :prospect="selectedProspect"
    :prospect-type="prospectType" :on-close="closePanel" :on-back="goBackToList" />

  <!-- Prospects List Panel -->
  <div v-else class="prospects-panel w-[640px] max-w-[640px] h-[100vh] flex flex-col bg-white">
    <div
      class="flex min-h-[50px] w-full max-w-[100vw] items-center gap-6 border-b border-b-[#EDF0FF] bg-neutral-200 px-[18px] py-2">
      <PanelHeaderNav :is-back-visible="false" :title="title" :on-close="closePanel" />
      <div class="flex flex-col shrink-0 items-self-center gap-1">
        <h2 class="shrink-0 text-2xl font-bold first-letter:capitalize">
          {{ title }}
        </h2>
      </div>
    </div>

    <div class="flex flex-col p-5">
      <!-- Search filter -->
      <CampaignsStatisticsPanelSearchbar />

      <!-- Loading state -->
      <div v-if="isLoading" class="flex justify-center py-8">
        <BcSpinner size="large" />
      </div>

      <!-- Prospects list -->
      <div v-else-if="prospects.length > 0" class="prospects-list w-full">
        <div class="grid grid-cols-[4fr_3fr_3fr_3fr] w-full text-xs">
          <!-- Grid Header -->
          <div class="grid-cell grid-header-cell">
            {{ $t('campaigns.statistics.prospects.headers.identity') }}
          </div>
          <div class="grid-cell grid-header-cell text-center">
            {{ $t('campaigns.statistics.prospects.headers.step') }}
          </div>
          <div class="grid-cell grid-header-cell text-center">
            {{ $t('campaigns.statistics.prospects.headers.campaign') }}
          </div>
          <div class="grid-cell grid-header-cell text-center">
            {{ $t('campaigns.statistics.prospects.headers.date') }}
          </div>
          <!-- Grid Rows -->
          <CampaignStatisticsProspectCard v-for="(prospect, index) in prospects" :key="prospect._id || index"
            :prospect="prospect" class="border-b last:border-b-0 border-blue-100" :index="index"
            @view-message="viewMessage(prospect)" :prospect-type="prospectType" />
        </div>
      </div>

      <!-- Empty state -->
      <div v-else class="text-center py-8 text-blue-900">
        {{ t('campaigns.prospects.no-prospects-found') }}
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.prospects-panel {
  overflow-y: auto;
}

.grid-header-cell {
  @apply py-3 px-6 text-left text-blue-400 font-medium uppercase;
}

.grid-cell {
  @apply px-2.5 py-1 h-[3.5rem] flex items-center;
}
</style>